import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ISrsBallotVote } from '../../shared/models/sr/srs-vote';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SrsBallotService } from '../../shared/services/srs-ballot.service';
import { SrsVoteId, srsVoteOption } from '../../shared/models/sr/srs-vote';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ISrSelectedVoteOption } from '../../shared/models/sr/sr-selected-vote-option';
import { exhaustMap, filter, map, Subject, Subscription } from 'rxjs';
import { ISubmitSrsProxyVotesRequest } from '../../shared/models/sr/submit-srs-proxy-votes-request';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { IApiResponse } from '../../../../shared/models/api-response';
import { IVoteOption } from '../../../../shared/models/ballot-vote';
import { ToastService } from '../../../../shared/services/toast.service';
import { IFileUpload } from '../../../controls/files-upload/files-upload.component';
import { IOption } from '../../../controls/models/option';

@Component({
    selector: 'app-srs-proxy-vote',
    templateUrl: './srs-proxy-vote.component.html',
    styleUrl: './srs-proxy-vote.component.scss',
    standalone: false
})
export class SrsProxyVoteComponent implements OnInit, OnDestroy {

  @Input() committeeMembers: ISrsBallotVote[] = [];
  srsVoteId = SrsVoteId;
  subscriptions: Subscription = new Subscription();
  authDocUploadProgress: number[] = [0];
  srsVoteOptions: {[key: string] : IVoteOption} = srsVoteOption;

  committeeMembersLabel: string = 'Committee Members';
  committeeMembersDescription: string = '';


  isSingleProxyVote: boolean = true;
  isSingleProxyChangeVote: boolean = false;
  leadProjectProposalOptions : IOption<boolean>[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  // Function to validate the Srs Proxy Vote Form
  srProxyVoteValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const reconfirmVoteOption: ISrSelectedVoteOption = (control.get('reconfirmVoteOptionCtrl') as FormControl).value;
    const reviseVoteOption: ISrSelectedVoteOption = (control.get('reviseVoteOptionCtrl') as FormControl).value;
    const withdrawVoteOption: ISrSelectedVoteOption = (control.get('withdrawVoteOptionCtrl') as FormControl).value;
    const hasVoteOption = reconfirmVoteOption?.isChecked || reviseVoteOption?.isChecked || withdrawVoteOption?.isChecked;

    const authDocUpload: IFileUpload = (control.get('authorisationDocumentCtrl') as FormControl).value;
    const hasAuthDoc = !authDocUpload?.hasError && authDocUpload?.files.length > 0 && authDocUpload?.files[0];

    return hasVoteOption && hasAuthDoc ? null : { invalidForm: true };
  };

  srsProxyVoteForm: FormGroup = new FormGroup({
    reconfirmVoteOptionCtrl: new FormControl({isChecked: false}),
    reviseVoteOptionCtrl: new FormControl({isChecked: false}),
    withdrawVoteOptionCtrl: new FormControl({isChecked: false}),
    leadProjectProposalCtrl: new FormControl(false),
    authorisationDocumentCtrl: new FormControl(),
    supportingInformationCtrl: new FormControl()
    },
    { validators: this.srProxyVoteValidator }
  );

  submit$: Subject<boolean> = new Subject<boolean>();

  constructor(private activeModal: NgbActiveModal,
              private _srsBallotService: SrsBallotService,
              private _toastService: ToastService)
              {}
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.isSingleProxyVote = this.committeeMembers.length === 1;
    this.isSingleProxyChangeVote = this.committeeMembers.length === 1 && this.committeeMembers[0].voteTypeId != SrsVoteId.SVT_NONE;
    this.onCommitteeMembersChanged(this.committeeMembers);
    this.setupFormSubscription();
    this.setupSubmitFormSubscription();
  }

  get reconfirmVoteOptionCtrl() {
    return this.srsProxyVoteForm.get('reconfirmVoteOptionCtrl') as FormControl;
  }
  get reviseVoteOptionCtrl() {
    return this.srsProxyVoteForm.get('reviseVoteOptionCtrl') as FormControl;
  }
  get withdrawVoteOptionCtrl() {
    return this.srsProxyVoteForm.get('withdrawVoteOptionCtrl') as FormControl;
  }
  get authorisationDocumentCtrl() {
    return this.srsProxyVoteForm.get('authorisationDocumentCtrl') as FormControl;
  }
  get leadProjectProposalCtrl() {
    return this.srsProxyVoteForm.get('leadProjectProposalCtrl') as FormControl;
  }
  get supportingInformationCtrl() {
    return this.srsProxyVoteForm.get('supportingInformationCtrl') as FormControl;
  }

  setupFormSubscription() {
    this.subscriptions.add(this.reconfirmVoteOptionCtrl.valueChanges.subscribe((formValue: Partial<ISrSelectedVoteOption>) => {
      if(formValue.isChecked) {
        this.reviseVoteOptionCtrl.patchValue({isChecked: false}, { emitEvent: false });
        this.withdrawVoteOptionCtrl.patchValue({isChecked: false}, { emitEvent: false });
      }
    }));

    this.subscriptions.add(this.reviseVoteOptionCtrl.valueChanges.subscribe((formValue: Partial<ISrSelectedVoteOption>) => {
      if(formValue.isChecked) {
        this.reconfirmVoteOptionCtrl.patchValue({isChecked: false}, { emitEvent: false });
        this.withdrawVoteOptionCtrl.patchValue({isChecked: false}, { emitEvent: false });
      }
    }));

    this.subscriptions.add(this.withdrawVoteOptionCtrl.valueChanges.subscribe((formValue: Partial<ISrSelectedVoteOption>) => {
      if(formValue.isChecked) {
        this.reconfirmVoteOptionCtrl.patchValue({isChecked: false}, { emitEvent: false });
        this.reviseVoteOptionCtrl.patchValue({isChecked: false}, { emitEvent: false });
      }
    }));

  }

  onCommitteeMembersChanged(committeeMembers: ISrsBallotVote[]) {
    this.setCommitteeMembersDescription(committeeMembers);
  }

  setCommitteeMembersDescription(committeeMembers: ISrsBallotVote[]) {
    if (committeeMembers.length > 0) {
      this.committeeMembersLabel = committeeMembers.length > 1 ? 'Committee Members:' : 'Committee Member:';


      let firstCm = committeeMembers[0].userName;
      let otherCms = '';

      if (committeeMembers.length > 1) {
        otherCms = ` + ${committeeMembers.length - 1} other${committeeMembers.length > 2 ? 's' : ''}`;
      }
      this.committeeMembersDescription = firstCm + otherCms;
    } else {
      this.committeeMembersDescription = '';
    }
  }

  submit(): void {
    this.submit$.next(true);
  }

  close(ballotId: string = '') {
    if (!ballotId || ballotId === '') {
      this.activeModal.close();
    } else {
      this.activeModal.close(ballotId);
    }
  }

  setupSubmitFormSubscription(): void {
    const errorToast: string = "Your vote was not placed due to an unexpected error. Please try again or contact IT Support if issue persists.";
    const errorToastTitle: string = "Vote Failed";

    const submitSubscription = this.submit$.pipe(
      map(() : ISubmitSrsProxyVotesRequest | null => {
        const isValid =
          this.srsProxyVoteForm.valid
          && this.committeeMembers
          && this.committeeMembers.length > 0;

        if (isValid) {
          return this.getSubmitRequest(this.srsProxyVoteForm.value)
        } else {
          return null;
        }
      }),
      filter((request) => request !== null),
      exhaustMap((request) => this._srsBallotService.submitProxyVotes(request as ISubmitSrsProxyVotesRequest)
    ))
    .subscribe({
      next: (event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            if (event.total) {
              this.authDocUploadProgress[0] = Math.round(
                (100 * event.loaded) / event.total
              );
            }
            break;
          case HttpEventType.Response:
            this.authDocUploadProgress[0] = 100;
            const apiResponse : IApiResponse<any> = event.body;
            if (apiResponse && apiResponse.success) {
              this._toastService.showSuccess("Your vote has been submitted. Thank you!", "Vote Successful");
            } else {
              console.log('Error submitting SR Proxy Vote.', apiResponse.message);
              this._toastService.showError(errorToast, errorToastTitle);
            }
            this.close(this.committeeMembers[0].ballotId)
            break;
        }
      },
      error: (error) => {
        this.authDocUploadProgress[0] = -1;
        console.log('Error submitting SR Proxy Vote.', error);
        this._toastService.showError(errorToast, errorToastTitle);
        this.close(this.committeeMembers[0].ballotId)
      }
    });

    this.subscriptions.add(submitSubscription);
  }

  getSubmitRequest(formData: IProxyVoteFormData): ISubmitSrsProxyVotesRequest | null {
    const selectedVoteOption =
      formData.reconfirmVoteOptionCtrl.isChecked ? formData.reconfirmVoteOptionCtrl :
      formData.reviseVoteOptionCtrl.isChecked ? formData.reviseVoteOptionCtrl :
      formData.withdrawVoteOptionCtrl.isChecked ? formData.withdrawVoteOptionCtrl : null;

    if (selectedVoteOption === null  || !this.committeeMembers || this.committeeMembers.length === 0) {
      return null;
    }

    const result : ISubmitSrsProxyVotesRequest = {
      ballotId: this.committeeMembers[0].ballotId,
      voteIds: this.committeeMembers.map(vote => vote.id),
      voteTypeId: selectedVoteOption.voteOptionId,
      comment:  !selectedVoteOption.comment || selectedVoteOption.comment === '' ? '' : selectedVoteOption.comment,
      currentPublicationConfirmation: true,
      leadProjectProposal: formData.leadProjectProposalCtrl === true ? true : false,
      authDoc: formData.authorisationDocumentCtrl.files[0],
      supportingInformationDoc: formData.supportingInformationCtrl?.files.length > 0 ? formData.supportingInformationCtrl.files[0] : null
    };

    return result;
  }
}

interface IProxyVoteFormData {
  reconfirmVoteOptionCtrl: ISrSelectedVoteOption;
  reviseVoteOptionCtrl: ISrSelectedVoteOption;
  withdrawVoteOptionCtrl: ISrSelectedVoteOption;
  leadProjectProposalCtrl: boolean;
  authorisationDocumentCtrl: IFileUpload;
  supportingInformationCtrl: IFileUpload;

}
